import { ampli } from '@/ampli';
import Image from 'next/image';
import { FunctionComponent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { TokenContentLinkType } from '@/lib/api/tokens/types';

import { CHAIN_EXPLORER_NAMES } from '@/constants/chain';

import { ZoraToken } from '@/hooks/useZora';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { BluechipIcon } from '@/ui/Icons/BluechipIcon';
import { BookIcon } from '@/ui/Icons/BookIcon';
import { ChainExplorerIcons, EtherscanIcon } from '@/ui/Icons/ChainExplorerIcon';
import { DefinedIcon } from '@/ui/Icons/DefinedIcon';
import { DiscordIcon } from '@/ui/Icons/DiscordIcon';
import { FarcasterIcon } from '@/ui/Icons/FarcasterIcon';
import { GlobeIcon } from '@/ui/Icons/GlobeIcon';
import { LinkedInIcon } from '@/ui/Icons/LinkedInIcon';
import { RedditIcon } from '@/ui/Icons/RedditIcon';
import { TelegramIcon } from '@/ui/Icons/TelegramIcon';
import { TwitterIcon } from '@/ui/Icons/TwitterIcon';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { getBlockExplorerTokenURL } from '@/utils/blockExplorers';
import { isZoraCreatorCoin } from '@/utils/zora';

import { ExternalLink } from '../../../ui/Link/ExternalLink';
import type { Links } from '../../../utils/coingecko/types';

type SupportedLink =
  | 'website'
  | 'twitter'
  | 'reddit'
  | 'telegram'
  | 'discord'
  | 'warpcast'
  | 'bluechip'
  | 'defined'
  | 'whitepaper'
  | 'docs'
  | 'linkedin';

const linkDefinitions: Record<SupportedLink, { Icon: FunctionComponent; label: string }> = {
  website: { Icon: GlobeIcon, label: 'Website' },
  twitter: { Icon: TwitterIcon, label: 'X.com' },
  reddit: { Icon: RedditIcon, label: 'Reddit' },
  telegram: { Icon: TelegramIcon, label: 'Telegram' },
  discord: { Icon: DiscordIcon, label: 'Discord' },
  warpcast: { Icon: FarcasterIcon, label: 'Farcaster' },
  bluechip: { Icon: BluechipIcon, label: 'Bluechip' },
  defined: { Icon: DefinedIcon, label: 'Defined' },
  whitepaper: { Icon: BookIcon, label: 'Whitepaper' },
  docs: { Icon: BookIcon, label: 'Docs' },
  linkedin: { Icon: LinkedInIcon, label: 'LinkedIn' },
};

type OverflowFade = 'none' | 'left' | 'right' | 'both';

const linksScrollerStyles = tv({
  base: ['overflow-x-auto scrollbar-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'],
  variants: {
    overflowFade: {
      none: '',
      left: 'mask-[linear-gradient(to_right,transparent_0,black_2rem)]',
      right: 'mask-[linear-gradient(to_right,black_calc(100%_-_2rem),transparent_100%)]',
      both: 'mask-[linear-gradient(to_right,transparent_0,black_2rem,black_calc(100%_-_2rem),transparent_100%)]',
    },
  },
});

const getOverflowFade = (scrollContainer: HTMLDivElement): OverflowFade => {
  const { clientWidth, scrollLeft, scrollWidth } = scrollContainer;
  const hasHiddenLinksBefore = scrollLeft > 1;
  const hasHiddenLinksAfter = scrollWidth - clientWidth - scrollLeft > 1;

  if (hasHiddenLinksBefore && hasHiddenLinksAfter) {
    return 'both';
  }

  return hasHiddenLinksBefore ? 'left' : hasHiddenLinksAfter ? 'right' : 'none';
};

interface TokenProfileAddtionalLinksProps {
  /** Token to display the links for */
  token?: TokenResult;
  /** A coingecko `Links` object. */
  coingeckoLinks: Links | undefined;
  /** Token Content API `Links` array. */
  tokenContentLinks?: TokenContentLinkType[] | undefined;
  /** Zora Token info */
  zora?: ZoraToken;
}

export const TokenProfileAdditionalLinks: React.FC<TokenProfileAddtionalLinksProps> = ({
  token,
  coingeckoLinks,
  tokenContentLinks,
  zora,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [overflowFade, setOverflowFade] = useState<OverflowFade>('none');

  const links = useMemo(() => {
    const { twitter_screen_name, homepage, telegram_channel_identifier, subreddit_url } =
      coingeckoLinks ?? {};

    const tokenLinksMap = (tokenContentLinks ?? []).reduce(
      (map, link) => ({ ...map, [link.linkType]: link }),
      {} as Partial<Record<SupportedLink, TokenContentLinkType>>,
    );

    if (!tokenLinksMap.website?.src && homepage?.length) {
      tokenLinksMap.website = { src: homepage[0], linkType: 'website' };
    }
    if (!tokenLinksMap.twitter?.src && twitter_screen_name) {
      const link = `https://x.com/${twitter_screen_name}`;
      tokenLinksMap.twitter = { src: link, linkType: 'twitter' };
    }
    if (!tokenLinksMap.telegram?.src && telegram_channel_identifier) {
      const link = `https://t.me/${telegram_channel_identifier}`;
      tokenLinksMap.telegram = { src: link, linkType: 'telegram' };
    }
    if (!tokenLinksMap.reddit?.src && subreddit_url && !subreddit_url.endsWith('reddit.com')) {
      tokenLinksMap.reddit = { src: subreddit_url, linkType: 'reddit' };
    }

    return Object.values(tokenLinksMap).filter(
      (link) => link.src && linkDefinitions[link.linkType as SupportedLink] != null,
    );
  }, [tokenContentLinks, coingeckoLinks]);

  const blockExplorerHref = token
    ? getBlockExplorerTokenURL(token.chainId, token.address)
    : undefined;
  const BlockExplorerIcon = ChainExplorerIcons[token?.chainId ?? 1] ?? EtherscanIcon;

  const zoraType = zora != null ? (isZoraCreatorCoin(token) ? 'creator' : 'post') : undefined;
  const zoraHref =
    zoraType === 'creator' && zora?.profile?.handle
      ? `https://zora.co/@${zora.profile.handle}`
      : zoraType === 'post' && zora?.coin?.address
        ? `https://zora.co/coin/base:${zora.coin.address}`
        : undefined;

  const handleLinkClick = (linkType: string) => {
    if (token == null) {
      return;
    }

    const { address, chainId, chainName } = token;
    ampli.tokenOfficialLinkClicked({
      link_type: linkType,
      token_address: address,
      token_chain_id: chainId,
      token_chain_name: chainName,
    });
  };

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const updateOverflowFade = () => {
      const nextOverflowFade = getOverflowFade(scrollContainer);
      setOverflowFade((current) => (current === nextOverflowFade ? current : nextOverflowFade));
    };

    updateOverflowFade();
    const resizeObserver = new ResizeObserver(updateOverflowFade);

    resizeObserver.observe(scrollContainer);
    scrollContainer.addEventListener('scroll', updateOverflowFade, { passive: true });

    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener('scroll', updateOverflowFade);
    };
  }, [blockExplorerHref, links, zoraHref]);

  return (
    <>
      <div className="flex flex-col gap-3">
        <h5 className={fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-1600' })}>
          Official resources
        </h5>
        <div
          ref={scrollContainerRef}
          aria-label="Official resource links"
          className={linksScrollerStyles({ overflowFade })}
          data-overflow-fade={overflowFade}
        >
          <div className="flex w-max flex-row flex-nowrap gap-2">
            {token && blockExplorerHref && (
              <ExternalLink
                key="explorer"
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'alphaGray',
                  class: 'flex-none gap-2',
                })}
                href={blockExplorerHref}
                onClick={() => handleLinkClick('explorer')}
              >
                <BlockExplorerIcon />
                {CHAIN_EXPLORER_NAMES[token.chainId]}
              </ExternalLink>
            )}
            {zoraHref && (
              <ExternalLink
                key="zora"
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'alphaGray',
                  class: 'flex-none gap-2',
                })}
                href={zoraHref}
                onClick={() => handleLinkClick('zora')}
              >
                <Image src="/images/tokens/zora.webp" alt="Zora" width={16} height={16} />
                Zora
              </ExternalLink>
            )}
            {links.map(({ linkType, src }) => {
              const { Icon, label } = linkDefinitions[linkType as SupportedLink];

              return (
                <ExternalLink
                  key={linkType}
                  className={buttons({
                    size: 'small',
                    shape: 'pill',
                    color: 'alphaGray',
                    class: 'flex-none gap-2',
                  })}
                  href={src}
                  onClick={() => handleLinkClick(linkType)}
                >
                  <Icon />
                  <span>{label}</span>
                </ExternalLink>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
